import React, {useEffect} from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";
import EditorToolbar from "./EditorToolbar";
import './EditorStyles.less';
import JsonPlugin from "./JsonPlugin";
import {useLexicalComposerContext} from "@lexical/react/LexicalComposerContext";
import LoadPlugin from "./LoadPlugin";
import BlockTagPlugin from "./BlockTagPlugin";
import WordCountPlugin from "./WordCounterPlugin";
import {LinkPlugin} from "@lexical/react/LexicalLinkPlugin";
import { CodeNode } from "@lexical/code";
import FloatingToolbar from "./FloatingToolbar";
import { $getRoot, $createParagraphNode, $createTextNode, $isElementNode } from "lexical";
import { $generateNodesFromDOM, $generateHtmlFromNodes } from "@lexical/html";
import { ParagraphNode, TextNode } from "lexical";
import ForceHeadingPlugin from "./ForceHeadingPlugin";
import TitleWatcherPlugin from "./TitleWatcherPlugin";
import {LexicalErrorBoundary} from "@lexical/react/LexicalErrorBoundary";

function EditorRefPlugin({ editorRef }) {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
        if (!editorRef) return;

        editorRef.current = {
            editor,
            waitReady: () =>
                new Promise((resolve, reject) => {
                    let tries = 0;
                    const tick = () => {
                        const rootEl = editor.getRootElement();
                        if (rootEl && document.contains(rootEl)) return resolve();
                        if (++tries > 100) return reject(new Error("Editor root never mounted"));
                        setTimeout(tick, 30);
                    };
                    tick();
                }),

            setHTML: (htmlString) => {
                if (!htmlString) return;

                const parser = new DOMParser();
                const doc = parser.parseFromString(htmlString, "text/html");

                editor.update(() => {
                    const root = $getRoot();
                    root.clear();

                    const nodes = $generateNodesFromDOM(editor, doc);

                    if (!nodes || nodes.length === 0) {
                        root.append($createParagraphNode().append($createTextNode(doc.body.textContent || "")));
                    } else {
                        for (const node of nodes) {
                            if ($isElementNode(node)) {
                                root.append(node);
                            } else {
                                const p = $createParagraphNode();
                                p.append(node);
                                root.append(p);
                            }
                        }
                    }

                    root.selectEnd();
                });
            },

            getHTML: () => {
                let html = "";
                editor.getEditorState().read(() => {
                    html = $generateHtmlFromNodes(editor);
                });
                return html;
            },
        };
    }, [editor, editorRef]);

    return null;
}

const editorConfig = {
    namespace: "ContentWriter",
    onError(error) {
        console.error(error);
    },
    theme: {
        text: {
            bold: "editor-text-bold",
            italic: "editor-text-italic",
            underline: "editor-text-underline",
            strikethrough: "editor-text-strikethrough",
        },
    },
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, CodeNode, ParagraphNode, TextNode],
};

export default function ContentEditor({ onJsonChange, editorRef, initialJson, rewriteWithAI, onTitleChange, onWordCountChange, onGenerateContent, contentBottom }) {
    return (
        <div className='editor' style={{position: 'relative'}}>
            <LexicalComposer initialConfig={editorConfig}>
                <div className='editor-toolbar-container'>
                    <EditorToolbar
                        rewriteWithAI={rewriteWithAI}
                        showGenerateContent={true}
                        onGenerateContent={onGenerateContent} />
                </div>
                <div className='editor-shell'>
                    <RichTextPlugin ErrorBoundary={LexicalErrorBoundary} contentEditable={
                        <ContentEditable
                            className="editor-input"
                            spellCheck={false}
                            autoCorrect="off"
                            autoCapitalize="off"
                            data-gramm="false"
                            data-gramm_editor="false"
                        />
                    }/>
                </div>
                <div className='editor-footer'>
                    {contentBottom}
                </div>
                <HistoryPlugin/>
                <ListPlugin/>
                <JsonPlugin onChange={onJsonChange}/>
                <EditorRefPlugin editorRef={editorRef}/>
                <LoadPlugin initialJson={initialJson}/>
                <BlockTagPlugin/>
                <WordCountPlugin onWordCountChange={onWordCountChange} />
                <LinkPlugin />
                <FloatingToolbar rewriteWithAI={rewriteWithAI} />
                <ForceHeadingPlugin />
                <TitleWatcherPlugin onTitleChange={onTitleChange} />
            </LexicalComposer>
        </div>
    );
}